'use client'

import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material'
import { isString } from 'lodash'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'

import { useDispatchActionMutation, useGetActionQuery, useRejectActionMutation } from '~/data/api/client/assistant'
import { type AssistantActionStatus, readActionStatus } from '~/data/api/client/assistant/enhancedEndpoints'
import useFormatters from '~/data/formatters'
import { logger } from '~/debug/logger'

import type { ActionProposal } from '../stream/dataParts'
import ArtifactDownload from './ArtifactDownload'

type ActionProposalPartProps = {
  proposal: ActionProposal
  /** A shared conversation: the viewer sees the action's progress but cannot decide on it. */
  readOnly: boolean
  /** Called once when a long-running action seen queued or running reaches its outcome. */
  onSettled: () => void
}

const STATUS_COLOR: Record<AssistantActionStatus, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  proposed: 'warning',
  queued: 'info',
  running: 'info',
  done: 'success',
  failed: 'error',
  rejected: 'default'
}

/** A dispatched action runs in the background; while it is queued or running its outcome is polled for. */
const OUTCOME_POLL_MS = 2000

/**
 * A mutation the assistant proposed. Its status is read from assistant-api rather than the transcript, so a
 * reopened conversation shows whether it was approved, rejected or has since run.
 */
export default function ActionProposalPart({ proposal, readOnly, onSettled }: ActionProposalPartProps) {
  const t = useTranslations('features.AssistantV2')
  const format = useFormatters()
  const [failed, setFailed] = useState(false)
  const [pollingInterval, setPollingInterval] = useState(0)
  const wasInFlight = useRef(false)
  const { actionId, longRunning } = proposal

  // `data` keeps the last action that loaded, including one seeded from the conversation, so a failed refetch
  // still leaves the decision controls on a proposed action.
  const {
    data: action,
    isError: isLoadError,
    isFetching,
    refetch
  } = useGetActionQuery({ actionId }, { pollingInterval })

  const status = readActionStatus(action?.status)

  // A long-running action's outcome is also posted to the transcript, so settling one asks for it.
  useEffect(() => {
    if (isInFlight(status)) {
      wasInFlight.current = true
      setPollingInterval(OUTCOME_POLL_MS)
    }
    if (isSettled(status)) {
      setPollingInterval(0)
      if (wasInFlight.current && longRunning) onSettled()
      wasInFlight.current = false
    }
  }, [status, longRunning, onSettled])
  const [dispatchAction, { isLoading: isDispatching }] = useDispatchActionMutation()
  const [rejectAction, { isLoading: isRejecting }] = useRejectActionMutation()

  const isBusy = isDispatching || isRejecting
  const canDecide = !readOnly && status === 'proposed'
  const preview = Object.entries(proposal.preview)

  const approve = async () => {
    setFailed(false)
    try {
      await dispatchAction({ actionId }).unwrap()
      setPollingInterval(OUTCOME_POLL_MS)
    } catch (error) {
      logger.error('Failed to dispatch an assistant action', error)
      setFailed(true)
    }
  }

  const reject = async () => {
    setFailed(false)
    try {
      await rejectAction({ actionId }).unwrap()
    } catch (error) {
      logger.error('Failed to reject an assistant action', error)
      setFailed(true)
    }
  }

  return (
    <Paper variant="outlined" data-testid="assistant-action-proposal" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="subtitle2">{t('actionTitle')}</Typography>
          {action && (
            <Chip
              size="small"
              color={status ? STATUS_COLOR[status] : 'default'}
              label={status ? format.assistantActionStatus(status) : action.status}
            />
          )}
        </Box>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {proposal.reason}
        </Typography>
        {proposal.actionType && (
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
            {proposal.actionType}
          </Typography>
        )}
        {preview.length > 0 && (
          <Box
            component="dl"
            sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.5 }}
          >
            {preview.map(([key, value]) => (
              <PreviewRow key={key} name={key} value={value} />
            ))}
          </Box>
        )}
        {status === 'done' && action?.outputSummary && <Alert severity="success">{action.outputSummary}</Alert>}
        {status === 'done' && action?.artifactKey && (
          <ArtifactDownload actionId={actionId} label={t('downloadArtifact')} />
        )}
        {status === 'failed' && <Alert severity="error">{action?.errorMessage || t('actionFailed')}</Alert>}
        {isLoadError && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" disabled={isFetching} onClick={() => void refetch()}>
                {t('retry')}
              </Button>
            }
          >
            {t('actionLoadError')}
          </Alert>
        )}
        {failed && <Alert severity="error">{t('actionError')}</Alert>}
        {canDecide && (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" color="inherit" disabled={isBusy} onClick={() => void reject()}>
              {t('reject')}
            </Button>
            <Button size="small" variant="contained" disabled={isBusy} onClick={() => void approve()}>
              {t('approve')}
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  )
}

function PreviewRow({ name, value }: { name: string; value: unknown }) {
  return (
    <>
      <Typography component="dt" variant="caption" sx={{ color: 'text.secondary' }}>
        {name}
      </Typography>
      <Typography component="dd" variant="caption" sx={{ m: 0, fontFamily: 'monospace', wordBreak: 'break-word' }}>
        {isString(value) ? value : JSON.stringify(value)}
      </Typography>
    </>
  )
}

function isInFlight(status: AssistantActionStatus | undefined): boolean {
  return status === 'queued' || status === 'running'
}

function isSettled(status: AssistantActionStatus | undefined): boolean {
  return status === 'done' || status === 'failed' || status === 'rejected'
}
